import React from 'react';
import { Box, Card, Row, Heading } from '../../core';
import { useParams } from 'react-router-dom';
import {
  useHlpStrategy,
  useCall2,
  useWeb3React,
  useChain,
  useAdmin,
  useTimelockByChain,
  useScionVault,
} from '@sc1/common';
import {
  AddressMethod,
  AmountMethod,
  SimpleMethod,
  BooleanAddressMethod,
  TwoAmountsMethod,
} from '../../methods';
import { HarvestStrategy } from '../HarvestStrategy';
import { DepositIntoStrategy } from '../DepositIntoStrategy';
import { WithdrawFromStrategy } from '../WithdrawFromStrategy';
import { StrategyInfo } from '../StrategyInfo';
import { RebalanceStrategy } from '../RebalanceStrategy';
import { RemoveLiquidity } from '../RemoveLiquidity';

export const HlpStrategy = (): JSX.Element | null => {
  // url params
  const { vaultAddress, strategyAddress } = useParams();
  const chain = useChain();

  // network
  const { account } = useWeb3React();

  // get abis
  const vault = useScionVault(vaultAddress);
  const strategy = useHlpStrategy(strategyAddress);
  const timelock = useTimelockByChain(chain);

  // admin
  const { isAdmin, isProposer, isVaultManager } = useAdmin();
  const { res: isStrategyManager } = useCall2(strategy?.isManager, account!);
  const { res: isGuardian } = useCall2(strategy?.isGuardian, account!);

  if (!isAdmin) return null;
  if (!strategy) return null;

  return (
    <Box>
      <StrategyInfo
        strategy={strategy}
        isProposer={isProposer}
        isVaultManager={isVaultManager}
        isStrategyManager={isStrategyManager}
        isGuardian={isGuardian}
      />

      {/* Vault Manager and Strategy Manager Methods */}
      <Row variant="collapse">
        <HarvestStrategy
          sx={{ flex: 1, my: 0 }}
          strategy={strategy}
          disabled={!isStrategyManager && !isGuardian} // cascading privileges
        />
        <Box m={1} />
        <RebalanceStrategy
          sx={{ flex: 1, my: 0 }}
          strategy={strategy}
          disabled={!isStrategyManager && !isGuardian} // cascading privileges
        />
      </Row>
      <Card>
        <Heading as="h3" mb={4}>
          Deposit &amp; Withdraw
        </Heading>
        <Row variant="collapse">
          <DepositIntoStrategy
            strategy={strategy}
            vault={vault}
            disabled={!isVaultManager}
          />
          <Box mx={4} my={3} />
          <WithdrawFromStrategy
            address={strategyAddress}
            strategy={strategy}
            vault={vault}
            disabled={!isVaultManager}
          />
        </Row>
      </Card>

      {/* Guardian Methods */}
      <AmountMethod
        method="setMaxTvl"
        label="Set Max TVL"
        contract={strategy}
        disabled={!isGuardian}
      />
      <AmountMethod
        method="setMaxDefaultPriceMismatch"
        label="Set Max Price Mismatch"
        contract={strategy}
        disabled={!isGuardian}
        decimals={4}
      />
      <AmountMethod
        method="closePosition"
        label="Close Position"
        contract={strategy}
        disabled={!isGuardian}
      />
      <SimpleMethod
        method="withdrawFromFarm"
        label="Withdraw From Farm"
        labelSubmit="Withdraw"
        contract={strategy}
        disabled={!isGuardian}
      />
      <BooleanAddressMethod
        method="setManager"
        label="Set Strategy Manager"
        labelTrue="Assign"
        labelFalse="Revoke"
        contract={strategy}
        disabled={!isGuardian}
      />
      <RemoveLiquidity strategy={strategy} disabled={!isGuardian} />
      <TwoAmountsMethod
        method="redeemCollateral"
        label="Redeem Collateral"
        amount1Label="Repay Amount"
        amount2Label="Withdraw Amount"
        contract={strategy}
        disabled={!isGuardian}
      />

      {/* Proposer Methods */}
      <BooleanAddressMethod
        method="setGuardian"
        label="Set Strategy Guardian"
        labelTrue="Assign"
        labelFalse="Revoke"
        contract={strategy}
        timelock={timelock}
        disabled={!isProposer}
      />
      <AmountMethod
        method="setSafeCollateralRatio"
        label="Set Safe Collateral Ratio"
        decimals={4}
        contract={strategy}
        timelock={timelock}
        disabled={!isProposer}
      />
      <AmountMethod
        method="setMinLoanHeath"
        label="Set Min Loan Health"
        contract={strategy}
        timelock={timelock}
        disabled={!isProposer}
        decimals={18}
      />
      <AmountMethod
        method="rebalanceThreshold"
        label="Set Rebalance Threshold"
        contract={strategy}
        timelock={timelock}
        disabled={!isProposer}
        decimals={4}
      />
      <AddressMethod
        method="setVault"
        label="Set Vault"
        contract={strategy}
        timelock={timelock}
        disabled={!isProposer}
      />
    </Box>
  );
};
